import React, { useEffect, useState, useMemo } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { Box, Alert } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { useAppMutation } from "../../../../services";
import parentServices from "../../../../services/parent-services";
import { useProfileContext } from "../context";

// ✅ Stripe publishable key
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY);

// ✅ Hardcoded client secret (test only!)
const TEST_CLIENT_SECRET =
  "seti_1S10sUIV5tBtYuKhEB4BhLHL_secret_Swuhvyg3QJXbEHdGtVq8koJ2EGxtNZg";

const CardFormInner = ({ hide, parentId }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [errMsg, setErrMsg] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { globalState, setGlobalState } = useProfileContext();

  const {
    mutate: setDefaultPaymentIntent,
    isSuccess,
    data,
    isError,
    isLoading: isLoadingPaymentIntent,
  } = useAppMutation(parentServices.setDefaultPaymentIntent);

  useEffect(() => {
    if (isSuccess) {
      console.log("✅ Payment method set:", data);
      setGlobalState({
        ...globalState,
        refreshList: true,
      });
      hide(false);
      setIsLoading(false);
    }
    if (isError) {
      setErrMsg("Failed to set default payment method");
    }
  }, [isSuccess, isError]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrMsg("");

    if (!stripe || !elements) return;

    setIsLoading(true);

    const result = await stripe.confirmSetup({
      elements,
      confirmParams: {
        // optional: add return_url if you want redirect
      },
      redirect: "if_required", // keeps it inline
    });

    if (result.error) {
      setErrMsg(result.error.message);
      setIsLoading(false);
    } else {
      // ✅ Setup successful
      console.log("SetupIntent:", result.setupIntent);
      console.log("PaymentMethod ID:", result.setupIntent.payment_method);

      const pmId = result.setupIntent.payment_method;
      setDefaultPaymentIntent({ parentId, paymentMethodId: pmId });
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
      <PaymentElement />
      {errMsg && <Alert severity="error">{errMsg}</Alert>}

      <LoadingButton
        type="submit"
        variant="contained"
        size="large"
        loading={isLoading}
        disabled={!stripe}
        sx={{
          mt: 2,
          bgcolor: "#2d6bbf",
          color: "#fff",
          "&:hover": { bgcolor: "#265ca6" },
        }}
      >
        Save Card
      </LoadingButton>
    </Box>
  );
};

const AboutForm = ({ hide }) => {
  const { globalState, setGlobalState } = useProfileContext();
  const [clientSecret, setClientSecret] = useState("");
  const [parentId, setParentId] = useState("");

  const {
    mutate: createSetupIntents,
    isSuccess,
    data,
    isError,
    isLoading,
  } = useAppMutation(parentServices.createSetupIntents);

  useEffect(() => {
    if (globalState.profile) {
      setParentId(globalState.profile.parentId);
    }
  }, [globalState]);

  useEffect(() => {
    if (parentId) {
      createSetupIntents({ parentId });
    }
  }, [parentId, createSetupIntents]);

  useEffect(() => {
    if (isSuccess) {
      console.log("---&&---", data);
      setClientSecret(data.secret);
    }
  }, [isSuccess, data]);

  const options = useMemo(
    () =>
      clientSecret
        ? { clientSecret, appearance: { theme: "stripe" } }
        : undefined,
    [clientSecret],
  );

  if (isLoading)
    return <Alert severity="info">Initializing secure card form…</Alert>;
  if (isError)
    return <Alert severity="error">Failed to create SetupIntent</Alert>;
  if (!clientSecret) return null;

  return (
    <Elements
      stripe={stripePromise}
      options={{
        clientSecret: clientSecret,
        appearance: {
          theme: "stripe",
          rules: {
            '.Tab[data-testid="payment-method-tab"]:not([id*="card"])': {
              display: "none",
            },
          },
        },
      }}
    >
      <CardFormInner hide={hide} parentId={parentId} />
    </Elements>
  );
};

export default AboutForm;
